import { useState } from "react";
import { Stack } from "@altimateai/lego";
import { IconAlertTriangle } from "@tabler/icons-react";
import { formatCurrencyAmount } from "./ownershipHelpers";
import type { TeamConflictsResponse } from "./types";
import ConflictingTeamsListModal from "./ConflictingTeamsListModal";

interface OwnershipConflictsBannerProps {
  conflicts: TeamConflictsResponse | null;
  isLoading: boolean;
  error: string | null;
}

const OwnershipConflictsBanner = ({
  conflicts,
  isLoading,
  error,
}: OwnershipConflictsBannerProps) => {
  const [showTeamsModal, setShowTeamsModal] = useState(false);

  // Don't show banner if loading, error, or no conflicts
  if (isLoading || error || !conflicts) {
    return null;
  }

  // Only show banner if there are actual conflicts
  if (
    conflicts.conflicted_team_count === 0 &&
    conflicts.conflicted_entity_count === 0 &&
    conflicts.conflicted_amount === 0
  ) {
    return null;
  }

  const hasTeamsList = conflicts.conflicted_teams && conflicts.conflicted_teams.length > 0;

  return (
    <>
      <div className="al-rounded-md al-bg-yellow-50 al-border al-border-yellow-200 al-p-3 al-mb-4">
        <Stack direction="row" align="start" gap="sm">
          <IconAlertTriangle
            size={16}
            className="al-text-yellow-600 al-mt-0.5 al-flex-shrink-0"
          />
          <div className="al-flex-1">
            <div className="al-text-sm al-text-foreground">
              Based on your current ownership rules, there are{" "}
              <span className="al-font-semibold">{conflicts.conflicted_entity_count} entities</span>{" "}
              from{" "}
              {hasTeamsList ? (
                <button
                  type="button"
                  onClick={() => setShowTeamsModal(true)}
                  className="al-font-semibold al-text-blue-600 hover:al-text-blue-800 hover:al-underline al-cursor-pointer"
                >
                  {conflicts.conflicted_team_count} teams
                </button>
              ) : (
                <span className="al-font-semibold">{conflicts.conflicted_team_count} teams</span>
              )}{" "}
              with ownership-rule conflicts, cross-teams, resulting in{" "}
              <span className="al-font-semibold">
                {formatCurrencyAmount(conflicts.conflicted_amount, conflicts.currency)}/month
              </span>{" "}
              of affected entity cost. Please review your configuration to resolve these conflicts.
            </div>
          </div>
        </Stack>
      </div>

      {hasTeamsList && (
        <ConflictingTeamsListModal
          open={showTeamsModal}
          onOpenChange={setShowTeamsModal}
          currency={conflicts.currency}
        />
      )}
    </>
  );
};

export default OwnershipConflictsBanner;
